import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import { downloadCSV as downloadGenericCSV } from '../../lib/functions';
import Button from '../Button';
import logger from "../../../utils/logger";
import LocationMapModal from '../../LocationMapModal';

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";

const ClockInOutReport = ({ clockEventsData = [], theme }) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();

    const [tableData, setTableData] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [summaryStats, setSummaryStats] = useState({});
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showMap, setShowMap] = useState(false);

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'date', label: 'Date' },
        { key: 'user_name', label: 'Employee' },
        { key: 'event_type', label: 'Event Type' },
        { key: 'timestamp', label: 'Time' },
        { key: 'address', label: 'Location' },
        { key: 'status', label: 'Status' },
        { key: 'accuracy', label: 'GPS Accuracy' },
        { key: 'driver_comments', label: 'Driver Comments' },
    ];

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);
    const [allowDownload, setAllowDownload] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true); // Open password modal on component mount
        //showData(); // for debugging
    }, []);
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();
                setAllowDownload(true); // Allow download if password is valid
            } else {
                logger.log("Invalid password.");
                setTableData([]); // Clear data if password is invalid
                showAlert({
                    message: 'Invalid password. Access denied.',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
            }
        } catch (error) {
            logger.error("Error validating password:", error);
            setTableData([]);
            showAlert({
                message: 'An error occurred while validating the password.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        }
        setOpenRequestPassword(false);
    };

    const sortByColumn = (column) => {
        const sorted = [...tableData].sort((a, b) => {
            const aVal = a[column]?.toString().toLowerCase() || '';
            const bVal = b[column]?.toString().toLowerCase() || '';
            return aVal.localeCompare(bVal);
        });
        setTableData(sorted);
    };

    const showData = () => {
        if (clockEventsData && clockEventsData.length > 0) {
            // Format the data for display
            const formatted = clockEventsData.map((event) => {
                // Format date to dd/mm/yyyy
                const dateObj = new Date(event.date);
                const formattedDate = isNaN(dateObj) 
                    ? event.date 
                    : `${String(dateObj.getDate()).padStart(2, '0')}/${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;
                
                // Format time to hh:mm
                const timeObj = new Date(event.timestamp);
                const formattedTime = isNaN(timeObj)
                    ? event.timestamp
                    : timeObj.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

                // Determine status based on event properties
                let status = 'Unknown';
                if (event.night_out) status = 'Night Out';
                else if (event.off_site) status = 'Off Site';
                else if (event.in_vehicle) status = 'In Vehicle';
                else if (event.depot_id) status = 'In Depot';
                else status = 'Standard';

                return {
                    date: formattedDate,
                    user_name: event.user_name || 'Unknown User',
                    event_type: event.event_type ? event.event_type.replace('_', ' ').toUpperCase() : 'UNKNOWN',
                    timestamp: formattedTime,
                    address: event.address || 'No Address Available',
                    status: status,
                    accuracy: event.accuracy ? `±${event.accuracy}m` : 'N/A',
                    driver_comments: event.driver_comments || '',
                    // Keep original data for map modal
                    originalEvent: event
                };
            });

            // Calculate summary statistics
            const stats = {
                totalEvents: formatted.length,
                uniqueEmployees: [...new Set(formatted.map(e => e.user_name))].length,
                clockInEvents: formatted.filter(e => e.event_type === 'CLOCK IN').length,
                clockOutEvents: formatted.filter(e => e.event_type === 'CLOCK OUT').length,
                breakStartEvents: formatted.filter(e => e.event_type === 'BREAK START').length,
                breakEndEvents: formatted.filter(e => e.event_type === 'BREAK END').length,
            };

            setTableData(formatted);
            setSummaryStats(stats);
            setIsFiltered(true);
        }
    };

    const handleLocationClick = (originalEvent) => {
        if (originalEvent && originalEvent.lat && originalEvent.lng) {
            setSelectedEvent(originalEvent);
            setShowMap(true);
        } else {
            showAlert({
                message: 'No location data available for this event.',
                type: 'warning',
                buttons: [{ type: 'secondary', label: 'Close' }],
            });
        }
    };

    const downloadCSV = () => {
        if (!allowDownload) {
            showAlert({
                message: 'Download not allowed. Please verify your password first.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
            return;
        }

        if (!tableData || tableData.length === 0) {
            showAlert({
                message: 'No data available for download.',
                type: 'warning',
                buttons: [{ type: 'warning', label: 'Close' }],
            });
            return;
        }

        const csvData = tableData.map(row => {
            const csvRow = {};
            displayColumns.forEach(({ key, label }) => {
                if (key === 'originalEvent') return; // Skip original event data
                
                // Special handling for time field to prevent Excel from converting it to a number
                if (key === 'timestamp') {
                    csvRow[label] = `'${row[key] || ''}`;  // Prefix with apostrophe to force text format
                } else {
                    csvRow[label] = row[key] || '';
                }
            });
            return csvRow;
        });

        downloadGenericCSV(csvData);
        
        showAlert({
            message: 'CSV file downloaded successfully!',
            type: 'success',
            buttons: [{ type: 'success', label: 'OK' }],
        });
    };

    // Get unique employees for filtering
    const uniqueEmployees = [...new Set(tableData.map(entry => entry.user_name))].sort();

    const getEventTypeStyle = (eventType) => {
        switch (eventType?.toLowerCase()) {
            case 'clock in':
                return { backgroundColor: '#d4edda', color: '#155724', fontWeight: 'bold' };
            case 'clock out':
                return { backgroundColor: '#f8d7da', color: '#721c24', fontWeight: 'bold' };
            case 'break start':
                return { backgroundColor: '#fff3cd', color: '#856404', fontWeight: 'bold' };
            case 'break end':
                return { backgroundColor: '#d1ecf1', color: '#0c5460', fontWeight: 'bold' };
            default:
                return { backgroundColor: isLightTheme ? '#f8f9fa' : '#333', color: isLightTheme ? '#333' : '#fff' };
        }
    };

    const getStatusStyle = (status) => {
        switch (status.toLowerCase()) {
            case 'in depot':
                return { color: '#0d6efd', fontWeight: 'bold' };
            case 'in vehicle':
                return { color: '#20c997', fontWeight: 'bold' };
            case 'off site':
                return { color: '#fd7e14', fontWeight: 'bold' };
            case 'night out':
                return { color: '#6f42c1', fontWeight: 'bold' };
            default:
                return { color: isLightTheme ? '#333' : '#fff' };
        }
    };

    return (
        <div className="container-fluid p-0">
            <RequestPassword
                theme={theme}
                isOpen={openRequestPassword}
                setIsOpen={setOpenRequestPassword}
                returnData={handlePasswordSubmission}
            />

            <LocationMapModal
                show={showMap}
                onHide={() => setShowMap(false)}
                location={selectedEvent}
                title="Clock Event Location"
            />

            {tableData && tableData.length > 0 && allowDownload ? (
                <div>
                    {/* Summary Stats */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                <div className={`card-header ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <h5 className="mb-0">🕐 Clock In/Out Report Summary</h5>
                                </div>
                                <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <div className="text-center">
                                                <h4 className="mb-1">{summaryStats.totalEvents}</h4>
                                                <small>Total Events</small>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="text-center">
                                                <h4 className="mb-1">{summaryStats.uniqueEmployees}</h4>
                                                <small>Unique Employees</small>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="text-center">
                                                <h4 className="mb-1 text-success">{summaryStats.clockInEvents}</h4>
                                                <small>Clock In Events</small>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="text-center">
                                                <h4 className="mb-1 text-danger">{summaryStats.clockOutEvents}</h4>
                                                <small>Clock Out Events</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mt-3">
                                        <div className="col-md-6">
                                            <div className="text-center">
                                                <h4 className="mb-1 text-warning">{summaryStats.breakStartEvents}</h4>
                                                <small>Break Start Events</small>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="text-center">
                                                <h4 className="mb-1 text-info">{summaryStats.breakEndEvents}</h4>
                                                <small>Break End Events</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Download Button */}
                    <div className="row mb-3">
                        <div className="col-12 text-center">
                            <Button
                                onClick={downloadCSV}
                                className={`btn ${isLightTheme ? 'btn-primary' : 'btn-outline-light'}`}
                            >
                                📥 Download CSV Report
                            </Button>
                        </div>
                    </div>

                    {/* Clock Events Table */}
                    <div className="row">
                        <div className="col-12">
                            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                <div className={`card-header ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <h5 className="mb-0">Clock In/Out Events Details ({tableData.length} events)</h5>
                                </div>
                                <div className="card-body p-0">
                                    <div className="table-responsive" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                        <table className={`table table-striped mb-0 ${isLightTheme ? '' : 'table-dark'}`}>
                                            <thead className={isLightTheme ? 'table-secondary' : 'table-light'} style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                                <tr>
                                                    {displayColumns.map(({ key, label }) => (
                                                        <th 
                                                            key={key} 
                                                            className="text-center"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => sortByColumn(key)}
                                                            title={`Click to sort by ${label}`}
                                                        >
                                                            {label}
                                                        </th>
                                                    ))}
                                                    <th className="text-center">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tableData.map((row, index) => (
                                                    <tr key={index}>
                                                        <td className="text-center">{row.date}</td>
                                                        <td className="text-center"><strong>{row.user_name}</strong></td>
                                                        <td className="text-center">
                                                            <span style={getEventTypeStyle(row.event_type)} className="px-2 py-1 rounded">
                                                                {row.event_type}
                                                            </span>
                                                        </td>
                                                        <td className="text-center"><strong>{row.timestamp}</strong></td>
                                                        <td className="text-center">{row.address}</td>
                                                        <td className="text-center">
                                                            <span style={getStatusStyle(row.status)}>
                                                                {row.status}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">{row.accuracy}</td>
                                                        <td className="text-center">
                                                            {row.driver_comments && row.event_type === 'CLOCK OUT' ? (
                                                                <div className="text-left" style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                                                                    <small><strong>{row.driver_comments}</strong></small>
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            {row.originalEvent && row.originalEvent.lat && row.originalEvent.lng ? (
                                                                <Button
                                                                    onClick={() => handleLocationClick(row.originalEvent)}
                                                                    className="btn btn-sm btn-outline-primary"
                                                                >
                                                                    📍 View Map
                                                                </Button>
                                                            ) : (
                                                                <span className="text-muted">No Location</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : !allowDownload ? (
                <div className="row">
                    <div className="col-12">
                        <div className={`alert ${isLightTheme ? 'alert-warning' : 'alert-dark'} text-center`}>
                            <h4>🔐 Access Required</h4>
                            <p>Please enter your password to view the clock in/out reports.</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="row">
                    <div className="col-12">
                        <div className={`alert ${isLightTheme ? 'alert-info' : 'alert-dark'} text-center`}>
                            <h4>🕐 No Clock Data Available</h4>
                            <p>No clock in/out data found for the selected date range. Try selecting a different date range.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClockInOutReport;